/**
 * Cloudflare Turnstile CAPTCHA for sign-in (Phase 5 lockout protection, user decision 2026-10-07).
 *
 * After 3 failed sign-ins for an email, POST /auth/login needs a Turnstile token. This widget
 * loads Cloudflare's script once (explicit rendering), renders the challenge and hands the token
 * to the parent. Tokens are single-use: change `resetKey` after every attempt to get a new one.
 *
 * Site key: REACT_APP_TURNSTILE_SITE_KEY (.env.development uses Cloudflare's always-pass test key).
 * SECURITY: the token is never logged.
 */

import React, { useEffect, useRef, useState } from 'react';

export interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}

export interface TurnstileApi {
  render: (container: HTMLElement | string, options: TurnstileRenderOptions) => string | null | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';
export const TURNSTILE_UNAVAILABLE_MESSAGE = 'Verification is unavailable. Contact your administrator.';
export const TURNSTILE_LOAD_FAILED_MESSAGE = 'Verification could not load. Check your connection and refresh the page.';

let scriptPromise: Promise<TurnstileApi> | null = null;

/** Load Cloudflare's script once per page; resolves with window.turnstile. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    const onLoad = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not initialise')));
    const onError = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error('Turnstile script failed to load'));
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

/** Test hook: forget the cached script promise. */
export function resetTurnstileLoaderForTests(): void {
  scriptPromise = null;
}

interface TurnstileWidgetProps {
  /** Receives the token, or null when it expires, errors or is reset. */
  onToken: (token: string | null) => void;
  /** Change this value to reset the widget and get a fresh token (tokens are single-use). */
  resetKey?: number;
  /** Defaults to REACT_APP_TURNSTILE_SITE_KEY. */
  siteKey?: string;
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onToken,
  resetKey = 0,
  siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const firstResetRef = useRef(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        const id = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
          theme: 'light',
        });
        widgetIdRef.current = id ?? null;
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      widgetIdRef.current = null;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey]);

  // A new resetKey means the last token was used: clear it and ask Cloudflare for a new one
  useEffect(() => {
    if (firstResetRef.current) {
      firstResetRef.current = false;
      return;
    }
    onTokenRef.current(null);
    const id = widgetIdRef.current;
    if (id && window.turnstile) window.turnstile.reset(id);
  }, [resetKey]);

  if (!siteKey) {
    return (
      <div role="alert" className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded text-sm">
        {TURNSTILE_UNAVAILABLE_MESSAGE}
      </div>
    );
  }
  if (loadFailed) {
    return (
      <div role="alert" className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded text-sm">
        {TURNSTILE_LOAD_FAILED_MESSAGE}
      </div>
    );
  }
  return <div ref={containerRef} data-testid="turnstile-widget" className="min-h-[65px]" />;
};

export default TurnstileWidget;
